import React, { useCallback, useEffect, useState } from 'react';
import { Toast, getClassNameFactory, getRehydratableName } from '@dcl/ui';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { IAnnouncementMessages } from './interface';
import { ANNOUNCEMENT_DBINDEX, ANNOUNCEMENT_STORE } from './utils/constants';
import { getDistinctObjects, renderAlertStateIcon } from './helper';
import ReactHtmlParser from 'react-html-parser';
import './index.css';

export const Announcement: React.FC<IAnnouncementMessages> = ({
  announcements,
}) => {
  const distinctAnnouncements = getDistinctObjects(announcements);

  const [isVisible, setIsVisible] = useState<{ [key: string]: boolean }>(
    Object.fromEntries(
      distinctAnnouncements.map((announcement) => [announcement.id, true])
    )
  );

  const request = indexedDB.open(ANNOUNCEMENT_DBINDEX, 2);

  // Extracted helper functions
  const deleteEntry = (objectStore, id) => {
    objectStore.delete(id);
    setIsVisible((prevVisibility) => ({
      ...prevVisibility,
      [id]: true,
    }));
  };

  const addEntry = (objectStore, id, expiry_date) => {
    objectStore.add({
      id: id,
      value: false,
      date: expiry_date,
    });

    setIsVisible((prevVisibility) => ({
      ...prevVisibility,
      [id]: false,
    }));
  };

  const updateSavedEntries = (
    objectStore,
    announcement_id,
    isDateExpired,
    getRequestResult
  ) => {
    if (isDateExpired) {
      deleteEntry(objectStore, announcement_id);
    } else {
      setIsVisible((prevVisibility) => ({
        ...prevVisibility,
        [announcement_id]: getRequestResult.value,
      }));
    }
  };

  const getAddDate = (expiryDate, nextDate) => {
    return expiryDate && expiryDate.length > 0
      ? new Date(expiryDate)
      : nextDate;
  };

  const isValidNonPersistent = (isDateNotExpired, isPersistent) => {
    return isDateNotExpired && !isPersistent;
  };

  const addVisiblePersistent = (isPersistent, announcement_id) => {
    if (isPersistent) {
      setIsVisible((prevVisibility) => ({
        ...prevVisibility,
        [announcement_id]: false,
      }));
    }
  };

  const processExistingAnnouncement = (
    objectStore,
    announcement,
    getRequestResult,
    currDate
  ) => {
    const savedDate = new Date(getRequestResult.date).setHours(0, 0, 0, 0);
    const isDateExpired = savedDate < currDate;
    updateSavedEntries(
      objectStore,
      announcement.id,
      isDateExpired,
      getRequestResult
    );
  };

  const processNewAnnouncement = (objectStore, announcement, currDate) => {
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + 90);
    const date = getAddDate(announcement.expiryDate, nextDate);

    const announcemnet_expiry_date = date.toLocaleString('en-us', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });

    const isDateNotExpired = date.setHours(0, 0, 0, 0) >= currDate;
    const isValid = isValidNonPersistent(
      isDateNotExpired,
      announcement.isPersistent
    );

    if (isValid) {
      addEntry(objectStore, announcement.id, announcemnet_expiry_date);
    }

    addVisiblePersistent(announcement.isPersistent, announcement.id);
  };

  useEffect(() => {
    const handleAnnouncement = (announcement) => {
      const db = request.result;
      const transaction = db.transaction([ANNOUNCEMENT_STORE], 'readwrite');
      const objectStore = transaction.objectStore(ANNOUNCEMENT_STORE);
      const getRequest = objectStore.get(announcement.id);
      const currDate = new Date().setHours(0, 0, 0, 0);

      getRequest.onsuccess = function () {
        if (getRequest.result) {
          processExistingAnnouncement(
            objectStore,
            announcement,
            getRequest.result,
            currDate
          );
        } else {
          processNewAnnouncement(objectStore, announcement, currDate);
        }
      };
    };

    request.onupgradeneeded = function () {
      const db = request.result;
      db.createObjectStore(ANNOUNCEMENT_STORE, {
        keyPath: 'id',
      });
    };

    request.onsuccess = function () {
      distinctAnnouncements.forEach(handleAnnouncement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (
    id: string,
    isPersistent: boolean,
    expiryDate: string
  ) => {
    if (isPersistent) {
      setIsVisible((prevVisibility) => ({
        ...prevVisibility,
        [id]: true,
      }));
      return;
    }
    const db = request.result;
    const transaction = db.transaction([ANNOUNCEMENT_STORE], 'readwrite');
    const objectStore = transaction.objectStore(ANNOUNCEMENT_STORE);

    const currentDate = new Date();
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + 90);

    const date =
      expiryDate && expiryDate.length > 0 ? new Date(expiryDate) : nextDate;

    const expiry_date = date.toLocaleString('en-us', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });

    const isDateExpired =
      date.setHours(0, 0, 0, 0) <= currentDate.setHours(0, 0, 0, 0);
    !isDateExpired &&
      objectStore.put({ id: id, value: true, date: expiry_date });

    transaction.oncomplete = function () {
      setIsVisible((prevVisibility) => ({
        ...prevVisibility,
        [id]: true,
      }));
    };
  };

  const remove = useCallback(
    (id: string, isPersistent: boolean, expiryDate: string) =>
      handleClick(id, isPersistent, expiryDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getClassName = getClassNameFactory(Announcement.displayName);

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(Announcement.displayName)}
      data-testid="announcement"
      {...propsToDataAttributes({ announcements })}
    >
      {distinctAnnouncements.map(
        (announcement) =>
          !isVisible[announcement.id] && (
            <Toast
              key={announcement.id}
              id={announcement.id}
              duration={0}
              type={renderAlertStateIcon(announcement.statusType)}
              remove={() =>
                remove(
                  announcement.id,
                  announcement.isPersistent,
                  announcement.expiryDate ?? ''
                )
              }
            >
              {ReactHtmlParser(announcement.message)}
            </Toast>
          )
      )}
    </div>
  );
};

Announcement.displayName = 'Announcement';
